import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Checkbox, 
  Container, 
  FormControl, 
  FormControlLabel, 
  Grid, 
  InputLabel, 
  MenuItem, 
  Select, 
  TextField, 
  Typography,
  Paper,
  Divider,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiService } from '../services/api.service';

const TenantProvisioning = () => {
  // Get today's date and one year from now in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  const oneYearFromNowStr = oneYearFromNow.toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    tenant_name: '',
    display_name: '',
    subscription_tier: 'standard',
    subscription_start_date: today,
    subscription_end_date: oneYearFromNowStr,
    max_users: 10,
    contact_email: '',
    contact_phone: '',
    notes: '',
    database: {
      tier: 'tier_1',
      custom_db_name: null,
      custom_schema: null
    },
    domains: [
      {
        domain_name: '',
        is_primary: true,
        ssl_enabled: true
      }
    ]
  });

  // These are the features that users can toggle
  const [selectedFeatures, setSelectedFeatures] = useState({
    forecasting: false,
    inventory: false,
    optimization: false,
    order_management: false,
    shipment: false
  });

  // These are the default features that are always enabled
  const defaultFeatures = [
    { id: 20, code: 'FLAG_MASTER_DATA_MODULE_001', name: 'Master Data' },
    { id: 21, code: 'FLAG_MESSAGING_MODULE_001', name: 'Messaging' },
    { id: 23, code: 'FLAG_REPORTING_MODULE_001', name: 'Reporting' },
    { id: 24, code: 'FLAG_INTEGRATION_MODULE_001', name: 'Integration' },
    { id: 25, code: 'FLAG_USER_MANAGEMENT_MODULE_001', name: 'User Management' },
    { id: 35, code: 'FLAG_CARRIER_MODULE_001', name: 'Carrier' },
    { id: 40, code: 'FLAG_CONFIG_MODULE_001', name: 'Configuration' }
  ];

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Map of feature names to their codes and IDs
  const featureMap = {
    forecasting: { code: 'FLAG_FORECASTING_MODULE_001', id: 18 },
    inventory: { code: 'FLAG_INVENTORY_MODULE_001', id: 17 },
    optimization: { code: 'FLAG_OPTIMIZATION_MODULE_001', id: 26 },
    order_management: { code: 'FLAG_ORDER_MANAGEMENT_MODULE_001', id: 19 },
    shipment: { code: 'FLAG_SHIPMENT_MODULE_001', id: 38 }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDatabaseChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      database: {
        ...formData.database,
        [name]: value
      }
    });
  };

  const handleDomainChange = (e, index = 0) => {
    const { name, value } = e.target;
    const updatedDomains = [...formData.domains];
    updatedDomains[index] = {
      ...updatedDomains[index],
      [name]: value
    };
    setFormData({
      ...formData,
      domains: updatedDomains
    });
  };

  const handleFeatureChange = (feature) => {
    setSelectedFeatures({
      ...selectedFeatures,
      [feature]: !selectedFeatures[feature]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Prepare features as objects with feature_code, feature_name, and is_enabled
      const features = [];
      
      // Add selected features that are enabled
      Object.entries(selectedFeatures).forEach(([feature, isSelected]) => {
        if (isSelected) {
          // Create a readable feature name from the feature key
          const featureName = feature
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
            
          features.push({
            feature_code: featureMap[feature].code,
            feature_name: featureName,
            is_enabled: true
          });
        }
      });
      
      // Add default features (always enabled)
      defaultFeatures.forEach(feature => {
        features.push({
          feature_code: feature.code,
          feature_name: feature.name,
          is_enabled: true
        });
      });
      
      // Prepare the final data according to what the backend expects
      const tenantData = {
        tenant_name: formData.tenant_name,
        display_name: formData.display_name,
        subscription_tier: formData.subscription_tier,
        subscription_start_date: formData.subscription_start_date,
        subscription_end_date: formData.subscription_end_date,
        max_users: parseInt(formData.max_users, 10),
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        notes: formData.notes,
        domain_name: formData.domains[0].domain_name,
        domains: formData.domains,
        features: features,
        database: {
          tier: formData.database.tier,
          host: formData.database.host || "",
          port: formData.database.port || 5432,
          custom_schema: formData.database.custom_schema || null
        }
      };
      
      // Use the API service instead of direct axios call
      const response = await apiService.post('/admin/tenants/provision', tenantData);
      setResult(response.data);
      setOpenSnackbar(true);
    
    } catch (err) {
      console.error('Error:', err);
      let errorMessage = 'An error occurred';
      
      if (err.response && err.response.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          errorMessage = typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail);
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Tenant Provisioning
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Create and provision a new tenant with selected features.
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Tenant Name"
                name="tenant_name"
                value={formData.tenant_name}
                onChange={handleInputChange}
                helperText="Internal name used for identification"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Display Name"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                helperText="Name shown to users"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Subscription Tier</InputLabel>
                <Select
                  name="subscription_tier"
                  value={formData.subscription_tier}
                  onChange={handleInputChange}
                  label="Subscription Tier"
                >
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Users"
                name="max_users"
                value={formData.max_users}
                onChange={handleInputChange}
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Subscription Start Date"
                name="subscription_start_date"
                value={formData.subscription_start_date}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Subscription End Date"
                name="subscription_end_date"
                value={formData.subscription_end_date}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Email"
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
            
            {/* Database Configuration */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Database Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Database Tier</InputLabel>
                <Select
                  name="tier"
                  value={formData.database.tier}
                  onChange={handleDatabaseChange}
                  label="Database Tier"
                >
                  <MenuItem value="tier_1">Tier 1 (High Performance)</MenuItem>
                  <MenuItem value="tier_2">Tier 2 (Standard)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Domain Configuration */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Domain Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Primary Domain"
                name="domain_name"
                value={formData.domains[0].domain_name}
                onChange={(e) => handleDomainChange(e, 0)}
                helperText="e.g., tenant.example.com"
              />
            </Grid>
            
            {/* Feature Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Feature Selection
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Optional Modules
              </Typography>
              <Box sx={{ ml: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedFeatures.forecasting}
                      onChange={() => handleFeatureChange('forecasting')}
                    />
                  }
                  label="Forecasting"
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedFeatures.inventory}
                      onChange={() => handleFeatureChange('inventory')}
                    />
                  }
                  label="Inventory"
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedFeatures.optimization}
                      onChange={() => handleFeatureChange('optimization')}
                    />
                  }
                  label="Optimization"
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedFeatures.order_management}
                      onChange={() => handleFeatureChange('order_management')}
                    />
                  }
                  label="Order Management"
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedFeatures.shipment}
                      onChange={() => handleFeatureChange('shipment')}
                    />
                  }
                  label="Shipment"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Default Modules (Always Enabled)
              </Typography>
              <List dense>
                {defaultFeatures.map((feature) => (
                  <ListItem key={feature.code}>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary={feature.name} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            
            {/* Submit Button */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                size="large"
                disabled={loading}
              >
                {loading ? 'Provisioning...' : 'Provision Tenant'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* Results Display */}
      {result && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Provisioning Results
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(result, null, 2)}
            </Typography>
          </Box>
        </Paper>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? "error" : "success"} 
          sx={{ width: '100%' }}
        >
          {error || "Tenant provisioned successfully!"}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TenantProvisioning; 